/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'underscore',
	'../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../../app/nls/StringResources'
], function($, _, UniqueId, StringResources) {

	class PropertiesUtil {

		/**
		 * Loops through an array of properties to add appropriate callbacks and default values
		 * @param {object} properties - the array of properties to process
		 * @param {ContentAPI} contentApi - content which wants to display these properties
		 * @return {Promise} resolved once all the properties have been processed
		 */
		processProperties(properties, contentApi, dashboardApi) {
			if (!properties) {
				return false;
			}

			const translationService = dashboardApi.getDashboardCoreSvc('TranslationService');

			const removeTranslationIcon = function (property) {
				// if the property has a tanslationIconNode defined then we were showing the missing translation dot.
				// Since we're just changed the property make sure the dot gets removed.
				if (translationService && property.translationIconNode) {
					translationService.removeTranslationIcon(property.translationIconNode);
					property.translationIconNode = null;
				}
			};

			const onChangeCallback = function(property, propertyName, propertyValue) {
				if( property.displayMultiplier ){
					propertyValue = Math.round(propertyValue / property.displayMultiplier);
				}
				this._callPropertyOnChangeCallback(property, propertyName, propertyValue);
				contentApi.setPropertyValue(propertyName, propertyValue);

				removeTranslationIcon(property);
			};

			const promises = [];

			let removedItems = 0;
			properties.slice().forEach((property, i) => {
				const customOnChangeCallback = typeof property['onChange'] === 'function' ? property.onChange.bind(property) : null;

				// Ignore hidden properties or the banner
				if (property.public === false || property.type === 'Banner') {
					return;
				}

				const propertiesOverride = this._getPropertiesOverride(property, contentApi);
				if (propertiesOverride.removeProperty === true) {
					properties.splice(i - removedItems, 1);
					removedItems++;
					return;
				}

				// Replace any overriden properties
				$.extend(property, propertiesOverride);

				// Property UI Controls always need a name property
				if (property.id && !property.name) {
					property.name = property.id;
				}

				// list of container types. Each section (ie. left, right) of a split has no type (undefined)
				const containerCtrlTypes = ['Group', 'CollapsibleSection', 'SingleLineLinks', 'Split', undefined];
				// list of static placeholders
				const staticCtrlTypes = ['text', 'icon', 'Separator', 'SectionLabel'];

				// child properties
				if (property.type === 'ColorPicker' && property.paletteType) {
					promises.push(this.handleColorPickerProperty(property, contentApi, dashboardApi));
				} else if (property.type === 'NewPalette' || property.type === 'Palette') {
					property.type = 'NewPalette';
					promises.push(this.handleNewPaletteProperty(property, contentApi, dashboardApi));
				} else if (property.type === 'ToggledCombo') {
					this._handleToggledComboProperty(property, contentApi);
				} else if (property.type === 'IconPicker') {
					this._handleIconPickerProperty(property, contentApi);
				} else if (property.module && property.module.indexOf('UiSlider') >= 0) {
					this._handleUISlider(property, contentApi);
				} else if (property.name === 'pageSizeWidth' || property.name === 'pageSizeHeight') {
					this._getPropertyDisplayValueWithUnits(contentApi, property);
				} else if (containerCtrlTypes.indexOf(property.type) > -1) {
					promises.push(this.processProperties(property.items, contentApi, dashboardApi));
				} else if (this._isALayoutPositionProperty(property.name)) {
					this._handleLayoutPositionalProperty(contentApi, property);
				}else if (property.id === 'rotateAngle') {
					let currentPropValue = contentApi.getPropertyValue(property.id);
					property.value = this.getRotateDisplayString(currentPropValue);
				} else if (staticCtrlTypes.indexOf(property.type) < 0) {
					let currentPropValue = contentApi.getPropertyValue(property.id);
					if (currentPropValue === null || currentPropValue === undefined) {
						currentPropValue = property.value;
					}
					if ((property.type === 'CheckBox') || (property.type === 'ToggleButton')) {
						if (currentPropValue === false) {
							property.checked = false;
						} else {
							property.checked = currentPropValue === true || property.defaultValue === true || (currentPropValue && currentPropValue === property.defaultValue);
						}
					} else {
						if (property.type === 'DropDown') {
							let valueToUse;
							/*
							* Enums - Dropdowns, are difficult because of Vida. Basically, we want
							* to check if the the current value returned is a valid. If it isn't,
							* use the property default. If it is, then we should check if it is an
							* object with property called 'name', which Vida enums do. If it does,
							* then use the name prop. All else fails, use the current prop value.
							*/
							if (currentPropValue !== null && currentPropValue !== undefined) {
								if (currentPropValue.hasOwnProperty('name')) {
									valueToUse = currentPropValue.name;
								} else {
									valueToUse = currentPropValue;
								}
							} else {
								valueToUse = property.defaultValue;
							}
							property.defaultValue = valueToUse;
						} else {
							property.value = (currentPropValue || currentPropValue === 0) ? currentPropValue : '';
						}
					}
					if( property.displayMultiplier && property.value ){
						let scaledValue = property.value * property.displayMultiplier;
						property.value = property.decimalPlaces ? scaledValue.toFixed(property.decimalPlaces) : Math.round(scaledValue);
					}
					property.onChange = property.onChange || onChangeCallback.bind(this, property);
				}

				if (customOnChangeCallback) {
					property.onChange = function (property, propertyName, propertyValue, options) {
						customOnChangeCallback(propertyName, propertyValue, options);
						removeTranslationIcon(property);
					}.bind(this, property);
				}

				// Replace any overriden properties once again in case we set defaults
				$.extend(property, propertiesOverride);
			});

			return Promise.all(promises);
		}

		/**
		 * Identifies if property name is top, left, width or height
		 * @param {String} property - the property name to be searched
		 * @return {boolean} - returns true or false based on property name
		 */
		_isALayoutPositionProperty(propertyName) {
			const positionNames = ['left', 'top', 'width', 'height'];
			return positionNames.includes(propertyName);
		}

		/**
		 * If the property object defines a 'preRenderCallback' property then call it to get any dynamic properties
		 * that should be applied to the property UI control
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 *
		 * @return {object} - returns the member variables (properties) to override
		 */
		_getPropertiesOverride(property, contentApi) {
			let propertiesOverride = {};

			// Call the callback to get any dynamic property info
			if (typeof property['preRenderCallback'] === 'function') {
				propertiesOverride = property.preRenderCallback(property, contentApi);
			}

			return propertiesOverride;
		}

		/**
		 * A property can define a custom onChange callback.  This is mostly used when one property change effects other properties,
		 * for example the show/hide legend property to hide/show the legened position property.
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {string} propertyName - the name of the property
		 * @param {object} propertyValue - the value of the property
		 */
		_callPropertyOnChangeCallback(property, propertyName, propertyValue) {
			if (property.onChangeCallback) {
				property.onChangeCallback(propertyName, propertyValue);
			}
		}

		/**
		 * Handles setting the callbacks and dynamic properties of a slider property control
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 */
		_handleUISlider(property, contentApi) {
			let currentPropValue = contentApi.getPropertyValue(property.name);
			if (currentPropValue || currentPropValue === 0 && property.sliderType === 'percentage') {
				currentPropValue = parseInt(currentPropValue * 100, 10);
			}

			property.start = currentPropValue || currentPropValue === 0? [currentPropValue] : [property.defaultValue];
			property.startLabels = [property.description || property.label];

			property.onChange = function(sliderValues) {
				// All our sliders currently only have one 'slide'
				var value = sliderValues[0];

				// The value with have the postfix at the end, for example 75%
				if (property.format && property.format.postfix) {
					value = value.substring(0, value.indexOf(property.format.postfix));
				}

				if (property.sliderType === 'percentage') {
					value = value * 0.01;
				}
				this._callPropertyOnChangeCallback(property, property.name, value);
				contentApi.setPropertyValue(property.name, value);
			}.bind(this);
		}

		/**
		 * Handles setting the callbacks and dynamic properties of a toggleCombo (auto refresh) property
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 */
		_handleToggledComboProperty(property, contentApi) {
			const propInfo = contentApi.getPropertyValue(property.name);
			if (!property.inputOptions) {
				property.inputOptions = {};
			}
			if (!property.checkBoxOptions) {
				property.checkBoxOptions = {};
			}
			if (!property.dropDownOptions) {
				property.dropDownOptions = {};
			}

			property.useToggleButton = true;

			if (propInfo) {
				property.dropDownOptions.defaultValue = propInfo.unit;
				property.inputOptions.value = propInfo.value;
				property.checkBoxOptions.checked = propInfo.autoRefresh;
			} else {
				property.inputOptions.value = property.inputOptions.defaultValue || '';
				property.checkBoxOptions.checked = false;
			}
			property.validateInput = this._validateToggleComboProperty.bind(this);

			property.onChange = function(propertyName, propertyValue) {
				this._callPropertyOnChangeCallback(property, propertyName, propertyValue);
				contentApi.setPropertyValue(propertyName, propertyValue);
			}.bind(this);
		}

		/**
		 * Handles setting the callbacks and dynamic properties of a IconPickerproperty
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 */
		_handleIconPickerProperty(property, contentApi) {
			if (!property.values) {
				const currentPropValue = contentApi.getPropertyValue(property.name);
				if (currentPropValue !== null && currentPropValue !== undefined) {
					property.values = currentPropValue;
				} else if (property.defaultValue !== undefined) {
					property.values = property.defaultValue;
				}
			}

			if (!property.onChange) {
				property.onChange = function(propertyName, propertyValue) {
					let values = propertyValue;
					if (propertyValue.length && propertyValue.length === 1) {
						values = propertyValue[0].name;
					} else if (propertyValue.length) {
						values = [];
						propertyValue.forEach((value)=>{
							values.push(value.name);
						});
					}

					this._callPropertyOnChangeCallback(property, propertyName, values);
					contentApi.setPropertyValue(propertyName, values);
				}.bind(this);
			}
		}

		/**
		 * @param timeUnit - 'seconds', 'minutes', 'hours'
		 * @returns a structure stating the min and max of the specified timeUnit
		 */
		_getMaxMinRefreshValuesForUnits(timeUnit) {
			/**
			 * The browser method used to provide a timed delay between refresh calls has
			 * a max size of a signed integer. We must ensure we stay below this number
			 * depending on the time unit
			 */
			const maxIntervalInSeconds = (Math.pow(2,31) - 1)/1000;
			let maxForTimeUnit;
			const values = {
			};
			switch (timeUnit) {
				case 'minutes':
					values.min = 1;
					maxForTimeUnit = maxIntervalInSeconds/60;
					values.max = Math.ceil(maxForTimeUnit - 1);
					break;
				case 'hours':
					values.min = 1;
					maxForTimeUnit = maxIntervalInSeconds/3600;
					values.max = Math.ceil(maxForTimeUnit - 1);
					break;
				default: // includes seconds
					values.min = 5;
					values.max = Math.ceil(maxIntervalInSeconds - 1);
			}
			return values;
		}

		/**
		 * Called when the toggleCombo (auto refresh) property changes
		 * @param {string} valueOfInput
		 * @param {string} valueOfDropDown
		 */
		_validateToggleComboProperty(valueOfInput, valueOfDropDown) {
			if (!valueOfInput || !valueOfDropDown) {
				return false;
			}

			let isValid = $.isNumeric(valueOfInput);
			if (isValid) {
				const maxMinValuesForUnit = this._getMaxMinRefreshValuesForUnits(valueOfDropDown);
				// ensure the valueOfInput is between our current min and max.
				isValid = (valueOfInput >= maxMinValuesForUnit.min) && (valueOfInput <= maxMinValuesForUnit.max);
			}

			return isValid;
		}

		/**
		 * Handles setting the callbacks and dynamic properties of a Palette property
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 * @param {object} dashboardApi
		 * @param {function} [onChangeCallback] - optional to override the default onchange behavior
		 */
		handleNewPaletteProperty(property, contentApi, dashboardApi, onChangeCallback) {
			if (!property.paletteType) {
				throw new Error('Palette property needs a "paletteType"', property);
			}
			const colorsService = dashboardApi.getFeature('Colors');

			const paletteId = (contentApi && contentApi.getPropertyValue(property.name)) ||
				property.defaultValue ||
				colorsService.getDefaultPaletteName(property.paletteType);

			let allowPaletteReverse = property.allowPaletteReverse !== false && (property.paletteType === 'HeatPalette' || property.paletteType === 'ConditionalPalette');
			let createPaletteType = property.paletteType === 'HeatPalette' ? 'continuous' : 'standard';
			let isReverse = property.isReversed ? property.isReversed : () => {
				let currentOrder = property.reversePalettePropName && contentApi.getPropertyValue(property.reversePalettePropName);
				return currentOrder === 'DarkerForLowerValue' && allowPaletteReverse;
			};
			property.menuItems = [];
			if (allowPaletteReverse) {
				property.menuItems.push('reverse');
			}
			property.reverse = isReverse();
			property.createPaletteType = createPaletteType;

			return colorsService.getPalette({
				paletteId: paletteId,
				type: property.paletteType,
				defaultPaletteId: property.defaultValue,
				addMissingBaseColors: property.addMissingBaseColors,
				defaultIfNotFound: true,
				forProperties: true
			}).then((selPalette) => {
				property.palette = selPalette;
				property.hasSection = true;
				property.readOnly = true;
				if(selPalette.my && !selPalette.public) {
					property.menuItems.push('edit');
				}

				if (!property.onChangePalette) {
					property.onChangePalette = onChangeCallback ? onChangeCallback : (propertyName, paletteItem) => {
						this._callPropertyOnChangeCallback(property, propertyName, paletteItem.id);
						contentApi.setPropertyValue(propertyName, paletteItem.id);
					};
				}

				// Handle selection when creating a palette
				let onCreatePalette = (paletteId) => {
					const prefixCMId = '__CM__';
					let cmId = prefixCMId + paletteId;
					property.onChangePalette(property.name, { id: cmId });
					return cmId;
				};
				property.onCreatePalette = onCreatePalette;
				if (property.onReversePalette) {
					property.onChange = property.onReversePalette;
				} else {
					property.onChange = (propertyName, paletteItem) => {
						var orderValue = paletteItem.reverse === true ? 'DarkerForLowerValue' : 'DarkerForHigherValue';
						this._callPropertyOnChangeCallback(property, property.reversePalettePropName, orderValue);
						contentApi.setPropertyValue(property.reversePalettePropName, orderValue);
					};
				}

				property.changePaletteCb = () => {
					dashboardApi.getDashboardSvc('propertiesManager').then((propertyManager) => {
						let options = {
							label: property.linkLabel,
							overlay: true,
							width: '320px',
							content: {
								module: 'authoring-common/changePaletteView',
								selectedId: selPalette.id,
								onCreatePalette: onCreatePalette,
								createPaletteType: createPaletteType,
								name: property.name,
								getPalettes: () => {
									return colorsService.getPalettes({
										type: property.paletteType,
										includeUserColorPalette: property.includeUserColorPalette,
										forProperties: true,
										includePatternPalettes: property.includePatternPalettes
									});
								},
								getPaletteId: property.getPaletteId ? property.getPaletteId : contentApi.getPropertyValue.bind(contentApi, property.id),
								reverse: isReverse(),
								onChange: property.onChangePalette,
								component: 'dashboard'
							}
						};
						dashboardApi.prepareGlassOptions(options);
						propertyManager.addChild(options);
					});
				};
				return property;
			});
		}

		/**
		 * Builds the payload needed for the property change event
		 * @param {string} propName - the name of the property changing
		 * @param {object} propValue - the new value for the property
		 */
		buildPropChangePayload(propName, propValue) {
			return {
				'category': propName,
				'item': propValue,
				'transactionId': _.uniqueId('prop_change')
			};
		}

		/**
		 * Add the ability to a color picker to handle custom colors
		 * @param {object} property - Property object
		 * @param {object} colorsService - ref to the color service
		 */
		addCustomColorCapability (property, colorsService) {
			property.createCustomColor = (hex) => {
				const className = colorsService.addCustomColor(hex);
				const colorProp = colorsService.createColorDefinition(className, hex, hex);
				colorProp.type = 'ColorCode';
				colorProp.hidden = true;
				return colorProp;
			};

			if (colorsService.isCustomColor(property.selectedName) && property.items) {
				const hex = colorsService.getHexColorFromClassName(property.selectedName);
				const colorProp = colorsService.createColorDefinition(property.selectedName, hex, hex);
				colorProp.hidden = true;
				property.items.push(colorProp);
			}
			return property;
		}

		/**
		 * Checks to see if the palette property is set in the model, if it isn't will return the default palette name for the type
		 * of palette being asked for
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 * @param {object} dashboardApi
		 * @param {string} palettePropertyName - the name of the property which contains the palette name
		 * @param {string} paletteType - the type of palette being asked for
		 */
		_getColorPalettePropertyValue(contentApi, colorsService, palettePropertyName, paletteType) {
			const propertyValue = contentApi.getPropertyValue(palettePropertyName);

			return propertyValue || colorsService.getDefaultPaletteName(paletteType);
		}

		/**
		 * Handles setting the callbacks and dynamic properties of a ColorPicker property
		 * @param {object} property - the property object for which to get any dynamically generated member variables
		 * @param {object} contentApi - the widget instance which wants to display these properties
		 * @param {object} dashboardApi
		 */
		handleColorPickerProperty(property, contentApi, dashboardApi) {
			if (!property.paletteType) {
				throw new Error('Color picker property needs a "paletteType"', property);
			}
			const colorsService = dashboardApi.getFeature('Colors');
			property.onChange = function(propertyName, propertyValueInfo) {
				this._callPropertyOnChangeCallback(property, property.propertyName, propertyValueInfo.name);
				contentApi.setPropertyValue(propertyName, propertyValueInfo.name);
			}.bind(this);

			const currentValue = contentApi.getPropertyValue(property.name);
			if (!property.selectedName) {
				if (typeof currentValue === 'number' || currentValue == null) {
					// when current value is 0, it should keep it
					property.selectedName = !currentValue && currentValue !== 0 && property.defaultValue ? property.defaultValue : currentValue;
				} else if (typeof currentValue === 'string') {
					// handle custom colors in hex format. Skip the colors which already has custom color names.
					if (colorsService.isCustomColor(currentValue)) {
						property.selectedName = currentValue;
					} else {
						// The default value on selectedName can be either a color class name or a custom color
						// If it ㅑs a custom color, ensure it gets added as a custom color class with a name
						property.selectedName = colorsService.getColorClassName(currentValue);
						if (colorsService.isCustomColor(property.selectedName) && property.addButton !== false) {
							// New custom color has been added.
							// Enable custom palettes, unless the property explicitly disallows custom picker
							property.addButton = true;
						}
					}
				}
			}

			return this._getColorPickerColors(property, contentApi, dashboardApi).then((colors) => {
				// If the colors being show are based off another property, then we need to tweak to payload for backwards compatibility
				if (property.palettePropertyName) {
					property.items = [];
					colors.forEach((color, index) => {
						property.items.push({
							'id': index,
							'name': index,
							'value': color.hexValue,
							'type': 'ColorCode',
							'label': color.label
						});
					});
				} else if (!property.items){
					property.items = colors;
				}
				//
				if(property.addButton){
					property = this.addCustomColorCapability(property, colorsService);
				}

				// If the current value saved in the model is higher then the number of colors available, default
				// back to the first color
				if (property.selectedName > colors.length) {
					property.selectedName = 0;
				} else if (!property.selectedName && property.selectedName !== 0 && property.defaultColorIndex !== undefined) {
					// when selectedName value is 0, it should keep it
					property.selectedName = property.defaultColorIndex;
				}

				return property;
			});
		}

		_getColorPickerColors(property, contentApi, dashboardApi) {
			const colorsService = dashboardApi.getFeature('Colors');

			if (property.paletteType === 'DashboardColorSet') {
				return Promise.resolve(colorsService.getDashboardColorSet().slice(0));
			}

			const paletteId = property.palettePropertyName ? this._getColorPalettePropertyValue(contentApi, colorsService, property.palettePropertyName, property.paletteType) : colorsService.getColorSetId();
			return colorsService.getPaletteColors({
				paletteId: paletteId,
				type: property.paletteType,
				addMissingBaseColors: property.addMissingBaseColors
			}).then((colors) => {
				return colors.slice(0);
			});
		}

		/**
		 * Currently only being called by the custom widget since it doesn't use contributions like our other widgets.
		 * Returns the default properties to show on the general property tab.
		 */
		getGeneralProperties(contentApi, dashboardApi) {
			const properties = [{
				'id': 'fillColor',
				'type': 'ColorPicker',
				'label': StringResources.get('propFillColor'),
				'showHexValue': true,
				'addButton': true,
				'open': false,
				'ariaLabel': StringResources.get('propFillColor'),
				'paletteType': 'ColorPalette',
				'defaultValue': 'color1',
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_appearance')
			}, {
				'id': 'borderColor',
				'type': 'ColorPicker',
				'label': StringResources.get('propBorderColor'),
				'open': false,
				'showHexValue': true,
				'addButton': true,
				'ariaLabel': StringResources.get('propBorderColor'),
				'paletteType': 'ColorPalette',
				'defaultValue': 'transparent',
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_appearance')
			}];

			if (contentApi) {
				return this.processProperties(properties, contentApi, dashboardApi)
					.then(() => {
						return properties;
					});
			} else {
				return Promise.resolve(properties);
			}
		}

		/**
		 * Method that takes our propertyUIControl spec and generates properties for our public API.  This is temporary until
		 * we clean up our properties logic throughout the code
		 * @param {*} properties
		 * @param {*} contentApi
		 * @param {*} dashboardApi
		 */
		propertyUIControlToPublicAPI(properties, contentApi, dashboardApi) {
			const propertiesList = [];
			const setValue = (property, value) => {
				if (dashboardApi.getMode() !== 'authoring') {
					dashboardApi.getGlassCoreSvc('.Logger').error('Trying to set a property without being in authoring mode.');
					return false;
				}
				if (property.customValidatorCallback) {
					const validInfo = property.customValidatorCallback(value);
					if (validInfo && validInfo.isValid === false) {
						dashboardApi.getGlassCoreSvc('.Logger').error(validInfo.message);
						return false;
					}
				}

				if (property.onChange) {
					if (property.module && property.module.indexOf('UiSlider') >= 0) {
						property.onChange([value]);
					} else if (property.type === 'ColorPicker') {
						const valueToSet = value && value.startsWith('#') ? dashboardApi.getFeature('Colors').addCustomColor(value) : value;
						property.onChange(property.name, {
							name: valueToSet
						});
					} else if (property.type === 'NewPalette') {
						property.onChangePalette(property.name, {
							id: value
						});
					} else {
						property.onChange(property.name, value);
					}

					return true;
				}
			};

			const getValue = (property, contentApi) => {
				const propValue = contentApi.getPropertyValue(property.name);
				if (propValue) {
					return propValue;
				}

				return property.defaultValue !== undefined ? property.defaultValue : null;
			};

			properties.forEach(property => {
				// For now guard against not having an onChange property.  This currently happens with complex property where the onChange is
				// buried inside of the items property.
				if (property.type === 'Banner' || property.public === false || !property.onChange) {
					return null;
				}

				// Currently only expose the minimum needed for system tests.  Once this starts being used for our UI we'll
				// need to augment what's exposed in the API
				const propertyApi = {
					name: property.name,
					getValue: getValue.bind(this, property, contentApi),
					setValue: setValue.bind(this, property)
				};

				propertiesList.push(propertyApi);
			});

			return propertiesList;
		}

		/**
		 * Creates a string using the value and units, intended for display. Appends a space if required. All other units except 'px' get a decimal place of 2
		 * @param {*} numericValue The value
		 * @param {*} units The units
		 * @param {*} addSpace Flag indicating if a space should be added
		 * @return {String} Returns a string, either empty or the value appended with the units
		 */
		getPropertyDisplayString(numericValue, units, addSpace) {
			if(numericValue !== null && numericValue !== undefined) {
				if(units) {
					units = units.trim();
					if(addSpace) {
						units = ` ${units}`;
					}
				}
				return String(Number(numericValue.toFixed((units === StringResources.get('pixelUnit')) ? 0 : 2))) + units;
			}
			return '';
		}

		/**
		 * Gets the display value for a property
		 * @param {*} contentApi The content API, used to retrieve the property value
		 * @param {*} property  The property
		 */
		_getPropertyDisplayValueWithUnits(contentApi, property) {
			let currentPropValue = contentApi.getPropertyValue(property.id);
			property.value = this.getPropertyDisplayString(currentPropValue, StringResources.get('pixelUnit'), true);
		}

		/**
		 * Gets the display value for a property with space
		 * @param {ContentAPI} contentApi The content API, used to retrieve the property value
		 * @param {Property} property  The property
		 */
		_handleLayoutPositionalProperty(contentApi, property) {
			let currentPropValue = contentApi.getPropertyValue(property.id);
			const numericValue = parseFloat(currentPropValue);
			property.value = this.getPropertyDisplayString(numericValue, currentPropValue.replace(/[\d.-]/g, ''), true);
		}

		/**
		 * Creates a string using the value, intended for display. Appends a space and units for rotation
		 * @param {propertyValue} propertyValue propertyValue
		 * @return {String} Returns rotate value formatted for display
		 */
		getRotateDisplayString(propertyValue) {
			const degreeUnit = String.fromCharCode(176);
			if(propertyValue !== null && propertyValue !== undefined) {
				return propertyValue + ' ' + degreeUnit;
			}
			return '';
		}

		_isValidPropertyId(id) {
			return /^[a-zA-Z0-9]*$/.test(id);
		}
	}

	return new PropertiesUtil();
});
